import { espnGameUrl } from './espn';
import { isOddsStub } from './espnIntel';
import { formatKickoffClock, isLocked } from './lock';
import { PLAYERS, playerFirstName, playerMeta, type PlayerId } from './roster';
import { pickProgress, picksForTeam, scoreSeason, scoreWeek } from './scoring';
import { loadTheme, THEMES, type BoardTheme } from './theme';
import type { Game, KeyPlayer, PickStore, WeekState } from './types';
import { parseWeekNumber, weekWindow } from './weeks';
import { morphChildren, type MNode } from './patch';

export interface UiHandles {
  root: HTMLElement;
}

const THEME_LABEL: Record<BoardTheme, string> = {
  night: 'Night look',
  ice: 'Ice look',
  jets: 'Jets look',
  chargers: 'Chargers look',
};

export function renderThemeChips(current: BoardTheme): string {
  return THEMES.map(
    (theme) =>
      `<button type="button" class="chip${current === theme ? ' chip--active' : ''}" data-theme-pick="${theme}" aria-pressed="${current === theme}">${THEME_LABEL[theme]}</button>`,
  ).join('');
}

export function syncThemeChips(
  buttons: Iterable<{
    dataset: { themePick?: string };
    classList: { toggle(token: string, force?: boolean): unknown };
    setAttribute(name: string, value: string): void;
  }>,
  theme: BoardTheme,
): void {
  for (const btn of buttons) {
    const on = btn.dataset.themePick === theme;
    btn.classList.toggle('chip--active', on);
    btn.setAttribute('aria-pressed', String(on));
  }
}

function formatKickoff(iso: string): string {
  return formatKickoffClock(iso);
}

function formatWeekTab(n: number): string {
  return `Week ${n}`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

function kickoffCountdown(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (!Number.isFinite(ms) || ms < 0 || ms > 48 * 60 * 60 * 1000) return '';
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0 ? `in ${hours}h ${minutes}m` : `in ${minutes}m`;
}

function renderKeyPlayers(players: KeyPlayer[] | undefined): string {
  if (!players?.length) return '';
  return `<ul class="key-players">
    ${players
      .map((p) => {
        const state =
          p.willPlay === false ? 'is-out' : p.willPlay === null ? 'is-maybe' : 'is-in';
        const word = p.willPlay === false ? 'Out' : p.willPlay === null ? 'Questionable' : 'Active';
        const pos = p.position ? ` ${escapeHtml(p.position)}` : '';
        return `<li class="${state}"><span>${escapeHtml(p.name)}</span>${pos} · <span class="player-status ${state}">${word}</span></li>`;
      })
      .join('')}
  </ul>`;
}

function intelRecord(game: Game, side: 'away' | 'home'): string {
  const rec = game.intel?.records?.[side];
  if (!rec || rec === '–') return '';
  const [wins, losses] = rec.split('-').map((part) => Number(part));
  const tone =
    Number.isFinite(wins) && Number.isFinite(losses)
      ? wins > losses
        ? ' record--win'
        : wins < losses
          ? ' record--loss'
          : ''
      : '';
  return ` <span class="record${tone}">(${escapeHtml(rec)})</span>`;
}

function renderIntel(game: Game): string {
  const intel = game.intel;
  const lean = intel?.predictor
    ? (() => {
        const home = intel.predictor.homePct >= intel.predictor.awayPct;
        const team = home ? game.home.short : game.away.short;
        const pct = Math.max(intel.predictor.homePct, intel.predictor.awayPct).toFixed(0);
        return `<span class="intel-chip">ESPN lean <strong class="intel-lean">${escapeHtml(team)} ${pct}%</strong></span>`;
      })()
    : '';
  const chips = [
    intel?.odds
      ? `${intel.odds.provider} ${intel.odds.details}${intel.odds.overUnder != null ? ` · O/U ${intel.odds.overUnder}` : ''}`
      : '',
    intel?.broadcast,
    intel?.weather,
  ]
    .filter((c): c is string => Boolean(c))
    .map((c) => `<span class="intel-chip">${escapeHtml(c)}</span>`);
  if (lean) chips.push(lean);

  const headline = intel?.blurb && !isOddsStub(intel.blurb) ? intel.blurb : '';

  return `
    ${headline ? `<p class="game-blurb">${escapeHtml(headline)}</p>` : ''}
    <div class="intel-row">${chips.join('')}</div>
  `;
}

function espnLink(game: Game): string {
  return `<a class="espn-link" href="${escapeHtml(espnGameUrl(game))}" target="_blank" rel="noopener noreferrer">Click here for detailed ESPN stats</a>`;
}

const LOCK_MARK = `<svg class="lock-icon" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="1.2" fill="currentColor"/><path d="M5.2 7V5.1a2.8 2.8 0 0 1 5.6 0V7" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>`;

export function computeBarPct(points: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((points / max) * 100)));
}

export function computeRankDelta(currentRank: number, priorRank?: number): number {
  return priorRank !== undefined ? priorRank - currentRank : 0;
}

export function renderRacerRow(opts: {
  id: PlayerId;
  rank: number;
  delta: number;
  name: string;
  pts: number;
  barPct: number;
  unit: string;
  isLeader: boolean;
}): string {
  const meta = playerMeta(opts.id);
  const deltaHtml =
    opts.delta > 0
      ? `<span class="delta-arrow delta-up" title="Up ${opts.delta} spots">▲${opts.delta}</span>`
      : opts.delta < 0
        ? `<span class="delta-arrow delta-down" title="Down ${Math.abs(opts.delta)} spots">▼${Math.abs(opts.delta)}</span>`
        : `<span class="delta-arrow delta-same" title="No rank change">—</span>`;

  return `
    <li class="racer-row${opts.isLeader ? ' leader' : ''}" data-key="${opts.id}" data-id="${opts.id}">
      <div class="player-meta">
        <span class="rank-num">${opts.rank}</span>
        ${deltaHtml}
        <div class="avatar-chip" style="--chip-bg: ${meta.chipBg}; --chip-border: ${meta.chipBorder};">
          ${meta.initial}
        </div>
        <span class="player-name" title="${escapeHtml(opts.name)}">${escapeHtml(opts.name)}</span>
      </div>
      <div class="bar-track-wrap">
        <div class="bar-track">
          <div class="bar-fill" data-pct="${opts.barPct}" style="width: ${opts.barPct}%;">
            <span class="bar-tip-glow"></span>
          </div>
        </div>
        <div class="score-badge">
          <span class="score-num">${opts.pts}</span>
          <span class="score-unit">${escapeHtml(opts.unit)}</span>
        </div>
      </div>
    </li>
  `;
}

export function renderSeasonStrip(
  weeks: WeekState[],
  adjustments: Partial<Record<PlayerId, number>> = {},
  currentWeekKey?: string,
): string {
  if (weeks.length === 0) return '';
  const season = [...scoreSeason(weeks, adjustments)].sort((a, b) => b.seasonPoints - a.seasonPoints);
  const totalGames = weeks.reduce((sum, w) => sum + w.games.length, 0);
  const leaderPts = season[0]?.seasonPoints ?? 0;
  const maxSeason = Math.max(1, totalGames, leaderPts);

  const sortedWeeks = [...weeks].sort((a, b) => parseWeekNumber(a.weekKey) - parseWeekNumber(b.weekKey));
  const currentWeekNum = currentWeekKey ? parseWeekNumber(currentWeekKey) : 0;
  const priorWeeks =
    sortedWeeks.length > 1
      ? currentWeekNum > 0
        ? sortedWeeks.filter((w) => parseWeekNumber(w.weekKey) < currentWeekNum)
        : currentWeekKey
          ? sortedWeeks.filter((w) => w.weekKey !== currentWeekKey)
          : sortedWeeks.slice(0, -1)
      : [];
  const priorRanks = new Map<PlayerId, number>();
  if (priorWeeks.length > 0) {
    const priorSeason = [...scoreSeason(priorWeeks, adjustments)].sort(
      (a, b) => b.seasonPoints - a.seasonPoints,
    );
    priorSeason.forEach((s, idx) => priorRanks.set(s.playerId, idx + 1));
  }

  return `
    <section class="season-strip" aria-label="Season standings">
      <h2 class="date-heading">Season Totals</h2>
      <ol class="standings-list">
        ${season
          .map((s, index) => {
            const isLeader = index === 0;
            const rank = index + 1;
            const prior = priorRanks.get(s.playerId);
            const delta = computeRankDelta(rank, prior);
            const barPct = computeBarPct(s.seasonPoints, maxSeason);
            return renderRacerRow({
              id: s.playerId,
              rank,
              delta,
              name: playerFirstName(s.playerId),
              pts: s.seasonPoints,
              barPct,
              unit: `pt${s.seasonPoints === 1 ? '' : 's'}`,
              isLeader,
            });
          })
          .join('')}
      </ol>
    </section>
  `;
}

function groupGamesByDate(games: Game[]): Map<string, Game[]> {
  const groups = new Map<string, Game[]>();
  for (const game of games) {
    const key = new Date(game.kickoffUtc).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/New_York',
    });
    const list = groups.get(key) ?? [];
    list.push(game);
    groups.set(key, list);
  }
  return groups;
}

export interface RenderOpts {
  espnError: string | null;
  liveWeek: number;
  seasonWeeks: WeekState[];
  seasonAdjustments?: Partial<Record<PlayerId, number>>;
  onRetry: () => void;
  onBackup: () => void;
  onReset: () => void;
  onTheme: (theme: BoardTheme) => void;
  onWeek: (week: number) => void;
}

export function renderSheetBanner(opts: {
  activeName: string;
  made: number;
  total: number;
  isPast: boolean;
}): string {
  const left = opts.total - opts.made;
  const label = opts.isPast
    ? `Viewing <strong>${escapeHtml(opts.activeName)}</strong>'s sheet (read-only) · ${opts.made} of ${opts.total} picks${left > 0 ? ` · ${left} left` : ''}`
    : `Editing <strong>${escapeHtml(opts.activeName)}</strong>'s sheet · ${opts.made} of ${opts.total} picks${left > 0 ? ` · ${left} left` : ''}`;
  const clearBtn = opts.isPast
    ? ''
    : `<button type="button" class="btn btn--ghost" id="btn-reset-banner">Clear ${escapeHtml(opts.activeName)}'s picks this week</button>`;

  return `
    <div class="sheet-banner" role="status">
      <span>${label}</span>
      ${clearBtn}
    </div>
  `;
}

export function renderApp(
  store: PickStore,
  handles: UiHandles,
  opts: RenderOpts,
): void {
  const week = store.getWeek();
  const active = store.getActivePlayer();
  const scores = scoreWeek(week.sheets, week.games);
  const sortedScores = [...scores].sort((a, b) => b.points - a.points);
  const activeName = playerFirstName(active);
  const progress = pickProgress(week.sheets[active], week.games);
  const currentWeekNum = parseWeekNumber(week.weekKey);
  const isPast = currentWeekNum < opts.liveWeek;

  const html = `
    <header class="app-header">
      <div class="header-bar">
        <div class="brand">
          <h1>Family Pick'em</h1>
          <nav class="week-strip" aria-label="NFL weeks">
            <span class="week-pill" data-key="week-pill" aria-hidden="true"></span>
            ${weekWindow(opts.liveWeek)
              .map((n) => {
                const selected = parseWeekNumber(week.weekKey) === n;
                const live = n === opts.liveWeek;
                return `<button type="button" class="week-tab${selected ? ' week-tab--current' : ''}${live ? ' week-tab--live' : ''}" data-week="${n}"${selected ? ' aria-current="page"' : ''}>${formatWeekTab(n)}${live ? ' <span class="week-live">This week</span>' : ''}</button>`;
              })
              .join('')}
          </nav>
        </div>
        <div class="header-actions">
          <div class="help-wrap">
          <button type="button" class="btn" id="btn-instructions" aria-expanded="false" aria-controls="instructions-panel">
            Instructions
          </button>
          <div class="app-menu" id="instructions-panel" hidden>
            <h2 class="menu-heading">How this works</h2>
            <ol class="faq">
              <li>Tap <strong>your</strong> first name at the top: Lisa, Chuck, Michael, Dave, Steven, Robert, or Shaun. The banner must say you are editing that person's sheet.</li>
              <li>For every game, tap the team you think will win. Other people's names show on the team they picked. The padlock on a team is your pick. The Locked badge is kickoff.</li>
              <li>Each game you get right is 1 point. NFL ties score nobody. Most points wins the week.</li>
              <li>You can change a pick until that game starts. At kickoff it locks. Nobody can change it after that, even if they are helping you.</li>
              <li>Helping someone else? Tap their name, fill their sheet, then tap your name again when you are done.</li>
              <li>Tapped the wrong name? Use Clear ${activeName}'s picks this week on that person's sheet. Games that already started stay.</li>
              <li>Stay on the tab marked This week. Later weeks are only for looking ahead. Season Totals is the running score.</li>
              <li>Anyone with this link can see and edit every sheet. Do not send it outside the family.</li>
            </ol>
          </div>
          </div>
          <div class="menu-wrap">
          <button type="button" class="menu-btn" id="btn-menu" aria-expanded="false" aria-controls="app-menu" aria-label="Open menu">
            <svg width="22" height="16" viewBox="0 0 22 16" aria-hidden="true"><path d="M1 2h20M1 8h20M1 14h20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
          <div class="app-menu" id="app-menu" hidden>
            <h2 class="menu-heading">Color look</h2>
            <p class="menu-help">Pick a board. Night is cyan and magenta. Ice is pink and ice-blue. Jets is green on black. Chargers is powder blue and gold.</p>
            <div class="theme-switch" role="group" aria-label="Color look">
              ${renderThemeChips(loadTheme())}
            </div>
            <h2 class="menu-heading">Sheet</h2>
            <button type="button" class="btn btn--ghost" id="btn-retry">Refresh games</button>
            <button type="button" class="btn" id="btn-backup">Download backup</button>
            ${isPast ? '' : `<button type="button" class="btn btn--ghost" id="btn-reset-sheet">Clear ${activeName}'s picks this week</button>`}
          </div>
          </div>
        </div>
      </div>
      ${renderSheetBanner({
        activeName,
        made: progress.made,
        total: progress.total,
        isPast,
      })}
    </header>

    <section class="player-chips" aria-label="Choose player sheet">
      ${PLAYERS.map(
        (p) => `
        <button
          type="button"
          class="chip${p.id === active ? ' chip--active' : ''}"
          data-player="${p.id}"
          aria-pressed="${p.id === active}"
        >${p.firstName}</button>`,
      ).join('')}
    </section>

    <section class="standings-strip" aria-label="Weekly standings">
      <h2 class="date-heading">Week ${parseWeekNumber(week.weekKey)} Standings</h2>
      <ol class="standings-list">
        ${sortedScores
          .map((s, index) => {
            const isLeader = index === 0;
            const rank = index + 1;
            const hole = pickProgress(week.sheets[s.playerId], week.games);
            const leftover =
              hole.total > 0 && hole.made < hole.total ? ` · ${hole.made}/${hole.total}` : '';
            const maxWeek = Math.max(1, week.games.length, sortedScores[0]?.points ?? 0);
            const barPct = computeBarPct(s.points, maxWeek);
            return renderRacerRow({
              id: s.playerId,
              rank,
              delta: 0,
              name: playerFirstName(s.playerId),
              pts: s.points,
              barPct,
              unit: `pt${s.points === 1 ? '' : 's'}${leftover}`,
              isLeader,
            });
          })
          .join('')}
      </ol>
    </section>

    ${renderSeasonStrip(opts.seasonWeeks, opts.seasonAdjustments, week.weekKey)}

    <main id="main" class="games-main">
      ${
        week.games.length === 0
          ? `<div class="empty-state"${opts.espnError ? ' role="alert"' : ''}>
              <p>${opts.espnError ? 'Could not load games from ESPN.' : 'No games this week yet.'}</p>
              ${opts.espnError ? `<p class="muted">${escapeHtml(opts.espnError)}</p><button type="button" class="btn" id="btn-retry-empty">Retry</button>` : ''}
            </div>`
          : `${
              opts.espnError
                ? `<div class="espn-banner" role="status">Could not refresh ESPN. Showing the last games. <button type="button" class="btn btn--ghost" id="btn-retry-empty">Retry</button></div>`
                : ''
            }${renderGames(store, active, isPast)}`
      }
    </main>
  `;

  const state = ROOT_STATE.get(handles.root);
  if (state && handles.root.querySelector('.app-header')) {
    state.opts = opts;
    state.store = store;
    state.handles = handles;
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    morphChildren(handles.root, tpl.content, keepClientAttr);
    return;
  }
  handles.root.innerHTML = html;
  ROOT_STATE.set(handles.root, { store, opts, handles });
  bindRoot(handles.root);
}

interface RootState {
  store: PickStore;
  opts: RenderOpts;
  handles: UiHandles;
}

const ROOT_STATE = new WeakMap<HTMLElement, RootState>();

/** Open/closed menu state lives on the client, so a repaint must not reset it. */
function keepClientAttr(el: MNode, attr: string): boolean {
  const node = el as unknown as Element;
  if (node.classList?.contains('week-pill')) return attr === 'style' || attr === 'data-ready';
  // Entrance stagger delay (motion/entrance.ts) must survive repaints until the flag clears.
  if (attr === 'style' && (node as HTMLElement).style?.getPropertyValue('--enter-delay')) return true;
  const id = node.getAttribute?.('id');
  if (id === 'app-menu' || id === 'instructions-panel') return attr === 'hidden';
  if (id === 'btn-menu') return attr === 'aria-expanded' || attr === 'aria-label';
  if (id === 'btn-instructions') return attr === 'aria-expanded';
  return false;
}

/** One delegated listener set, bound once, so repaints never re-attach handlers. */
function bindRoot(root: HTMLElement): void {
  const setPanelOpen = (panelId: 'app-menu' | 'instructions-panel', open: boolean) => {
    const panel = root.querySelector<HTMLElement>(`#${panelId}`);
    const btn = root.querySelector<HTMLButtonElement>(
      panelId === 'app-menu' ? '#btn-menu' : '#btn-instructions',
    );
    if (!panel || !btn) return;
    panel.toggleAttribute('hidden', !open);
    btn.setAttribute('aria-expanded', String(open));
    if (panelId === 'app-menu') {
      btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    }
  };
  const closePanels = () => {
    setPanelOpen('app-menu', false);
    setPanelOpen('instructions-panel', false);
  };

  root.addEventListener('click', (ev) => {
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const st = ROOT_STATE.get(root);
    if (!st) return;
    const { store, opts } = st;

    if (target.closest('#btn-menu')) {
      const opening = Boolean(root.querySelector('#app-menu')?.hasAttribute('hidden'));
      setPanelOpen('instructions-panel', false);
      setPanelOpen('app-menu', opening);
      return;
    }
    if (target.closest('#btn-instructions')) {
      const opening = Boolean(root.querySelector('#instructions-panel')?.hasAttribute('hidden'));
      setPanelOpen('app-menu', false);
      setPanelOpen('instructions-panel', opening);
      return;
    }
    if (!target.closest('.app-menu')) closePanels();

    const player = target.closest<HTMLElement>('[data-player]');
    if (player) {
      store.setActivePlayer(player.dataset.player as PlayerId);
      return;
    }
    const themeBtn = target.closest<HTMLElement>('[data-theme-pick]');
    if (themeBtn) {
      opts.onTheme(themeBtn.dataset.themePick as BoardTheme);
      return;
    }
    const weekBtn = target.closest<HTMLElement>('[data-week]');
    if (weekBtn) {
      const n = Number(weekBtn.dataset.week);
      if (Number.isInteger(n)) opts.onWeek(n);
      return;
    }
    const pick = target.closest<HTMLButtonElement>('[data-pick]');
    if (pick) {
      if (pick.disabled) return;
      if (parseWeekNumber(store.getWeek().weekKey) < opts.liveWeek) return;
      void store.setPick(pick.dataset.game!, pick.dataset.pick!);
      return;
    }
    if (target.closest('#btn-retry') || target.closest('#btn-retry-empty')) opts.onRetry();
    else if (target.closest('#btn-backup')) opts.onBackup();
    else if (target.closest('#btn-reset-sheet') || target.closest('#btn-reset-banner')) {
      if (parseWeekNumber(store.getWeek().weekKey) < opts.liveWeek) return;
      opts.onReset();
    }
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') closePanels();
  });
}

function renderGames(store: PickStore, active: PlayerId, readOnly = false): string {
  const week = store.getWeek();
  const groups = groupGamesByDate(week.games);
  const sheet = week.sheets[active];

  return Array.from(groups.entries())
    .map(([dateLabel, games]) => {
      const cards = games.map((game) => renderGameCard(game, sheet, week.sheets, readOnly)).join('');
      return `<section class="date-group" data-key="${dateLabel}"><h2 class="date-heading">${dateLabel}</h2>${cards}</section>`;
    })
    .join('');
}

export function renderGameCard(
  game: Game,
  sheet: { picks: Record<string, string>; tiebreaker?: number },
  allSheets: Record<PlayerId, import('./types').WeekSheet>,
  readOnly = false,
): string {
  const locked = isLocked(game.kickoffUtc);
  const picked = sheet.picks[game.id];
  const byTeam = picksForTeam(game.id, allSheets);
  const awayPickers = (byTeam.get(game.away.id) ?? []).map(playerFirstName).join(', ');
  const homePickers = (byTeam.get(game.home.id) ?? []).map(playerFirstName).join(', ');
  const winner =
    game.status === 'final' && game.awayScore !== undefined && game.homeScore !== undefined
      ? game.awayScore > game.homeScore
        ? game.away.id
        : game.homeScore > game.awayScore
          ? game.home.id
          : ''
      : '';
  const won = (teamId: string) =>
    winner === teamId ? '<span class="winner-mark" aria-label="Won">✓</span>' : '';
  const scoreLine =
    game.status === 'final' && game.awayScore !== undefined && game.homeScore !== undefined
      ? `<p class="final-score">Final: ${game.away.short} ${game.awayScore} – ${game.home.short} ${game.homeScore}</p>`
      : game.status === 'in'
        ? `<p class="live-score">Live: ${game.away.short} ${game.awayScore ?? '–'} – ${game.home.short} ${game.homeScore ?? '–'}</p>`
        : '';

  const countdown = kickoffCountdown(game.kickoffUtc);

  return `
    <article class="game-card${locked ? ' game-card--locked' : ''}" data-game="${game.id}" data-key="${game.id}">
      <header class="game-card__head">
        <time datetime="${game.kickoffUtc}">${formatKickoff(game.kickoffUtc)}</time>
        ${countdown ? `<span class="kickoff-eta">${escapeHtml(countdown)}</span>` : ''}
        ${locked ? `<span class="lock-badge" aria-label="Locked">${LOCK_MARK} Locked</span>` : ''}
      </header>
      ${scoreLine}
      ${renderIntel(game)}
      <div class="matchup">
        <button
          type="button"
          class="team-btn${picked === game.away.id ? ' team-btn--picked' : ''}"
          data-game="${game.id}"
          data-pick="${game.away.id}"
          ${locked || readOnly ? 'disabled' : ''}
          aria-pressed="${picked === game.away.id}"
        >
          ${game.away.logo ? `<img src="${game.away.logo}" alt="" class="team-logo" width="32" height="32" />` : ''}
          <span class="team-short">${picked === game.away.id ? '<span class="pick-lock" aria-hidden="true">🔒</span>' : ''}${game.away.short}${won(game.away.id)}</span>
          <span class="team-name">${game.away.name}${intelRecord(game, 'away')}</span>
          ${awayPickers ? `<span class="picker-names">${awayPickers}</span>` : ''}
          ${renderKeyPlayers(game.intel?.awayPlayers)}
        </button>
        <span class="at">@</span>
        <button
          type="button"
          class="team-btn${picked === game.home.id ? ' team-btn--picked' : ''}"
          data-game="${game.id}"
          data-pick="${game.home.id}"
          ${locked || readOnly ? 'disabled' : ''}
          aria-pressed="${picked === game.home.id}"
        >
          ${game.home.logo ? `<img src="${game.home.logo}" alt="" class="team-logo" width="32" height="32" />` : ''}
          <span class="team-short">${picked === game.home.id ? '<span class="pick-lock" aria-hidden="true">🔒</span>' : ''}${game.home.short}${won(game.home.id)}</span>
          <span class="team-name">${game.home.name}${intelRecord(game, 'home')}</span>
          ${homePickers ? `<span class="picker-names">${homePickers}</span>` : ''}
          ${renderKeyPlayers(game.intel?.homePlayers)}
        </button>
      </div>
      ${espnLink(game)}
    </article>
  `;
}
